import { action, computed, makeObservable, observable } from 'mobx'

import { ISitemapItem, ISitemapItemInput } from '~/client/graph'
import SitemapItem from '~/client/src/shared/models/SitemapItem'
import {
  DELETE_SITEMAP_ITEMS,
  SAVE_SITEMAP_ITEM,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import Guard from '~/client/src/shared/utils/Guard'

export default class SitemapItemsStore {
  @observable public isDataReceived = false

  public constructor(private eventsStore: EventsStore) {
    makeObservable(this)
    Guard.requireAll({
      eventsStore,
    })
  }

  @computed
  public get list(): SitemapItem[] {
    return Array.from(this.byId.values())
  }

  public get byId() {
    return this.eventsStore.appState.sitemapItems
  }

  /**
   * The one way to find a location's pin: by location id (STX1-1187); the legacy
   * assignedTo kind is neither read nor sent (STX1-1355). Derived from byId, so a delete or
   * a re-pointed pin cannot leave a stale entry behind (the old kind-keyed index deleted by
   * item id, not by its key).
   * Every reader wants coordinates, so an unplaced duplicate (the residue of a re-kinded
   * location re-planned by Map Setup) never hides the placed pin.
   */
  @computed
  public get byAssignedId(): Map<string, SitemapItem> {
    const map = new Map<string, SitemapItem>()
    for (const item of this.byId.values()) {
      if (!item.assignedId) continue
      if (!item.coordinates && map.get(item.assignedId)?.coordinates) continue
      map.set(item.assignedId, item)
    }
    return map
  }

  @action.bound
  public clearList() {
    this.isDataReceived = false
    this.byId.clear()
  }

  @action.bound
  public receiveList(list: ISitemapItem[]) {
    this.clearList()

    list.forEach(dto => {
      const item = SitemapItem.fromDto(dto)
      this.byId.set(item.id, item)
    })

    this.isDataReceived = true
  }

  @action.bound
  public receiveOne(id: string, dto: ISitemapItem) {
    if (dto) {
      this.byId.set(dto.id, SitemapItem.fromDto(dto))
    } else {
      this.byId.delete(id)
    }
  }

  @action.bound
  public applyOptimisticSave(sitemapItem: SitemapItem, id?: string): void {
    const resolvedId = id ?? sitemapItem?.id
    if (!resolvedId) {
      return
    }
    sitemapItem.id = resolvedId
    this.byId.set(resolvedId, sitemapItem.copy())
  }

  @action.bound
  public save(
    sitemapItem: SitemapItem,
    callbackFn?: (id: string) => void,
    errorCallbackFn?: (error: unknown) => void,
  ) {
    const { id: projectId } = this.eventsStore.appState.activeProject

    const sitemapItemInput: ISitemapItemInput = {
      id: sitemapItem.id,
      name: sitemapItem.name,
      color: sitemapItem.color,
      iconName: sitemapItem.iconName,
      type: sitemapItem.type,
      assignedId: sitemapItem.assignedId,
      // By id only: neither assignedTo nor parent.parentType is sent (STX1-1355).
      parent: sitemapItem.parent
        ? { parentId: sitemapItem.parent.parentId }
        : null,
      projectId,
      coordinates: sitemapItem.coordinates,
      shapeCoordinates: sitemapItem.shapeCoordinates,
    }

    this.eventsStore.dispatch(
      SAVE_SITEMAP_ITEM,
      sitemapItemInput,
      callbackFn,
      errorCallbackFn,
    )
  }

  @action.bound
  public removeMany(sitemapItemIds: string[], callbackFn?: () => void) {
    if (!sitemapItemIds?.length) {
      return
    }

    this.eventsStore.dispatch(DELETE_SITEMAP_ITEMS, sitemapItemIds, callbackFn)
  }
}
